import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Users, UserCheck, UserX, Plus } from 'lucide-react';
import { gameRosterService, GameRosterPlayer } from '@/services/gameRosterService';
import { toast } from 'sonner';

interface GameRosterManagementProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  game: any;
  onRosterUpdate?: () => void;
}

export const GameRosterManagement: React.FC<GameRosterManagementProps> = ({
  isOpen,
  onClose,
  gameId,
  game,
  onRosterUpdate
}) => {
  const [roster, setRoster] = useState<GameRosterPlayer[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRoster = async () => {
    try {
      setLoading(true);
      const data = await gameRosterService.loadGameRoster(gameId);
      setRoster(data);
    } catch (error) {
      console.error('Error loading roster:', error);
      toast.error('Failed to load game roster');
    } finally {
      setLoading(false);
    }
  };

  const populateFromTeams = async () => {
    try {
      setLoading(true);
      await gameRosterService.populateGameRosterFromTeams(
        gameId,
        game?.home_team_id,
        game?.away_team_id
      );
      await loadRoster();
      toast.success('Roster populated from team rosters');
      onRosterUpdate?.();
    } catch (error) {
      console.error('Error populating roster:', error);
      toast.error('Failed to populate roster');
    } finally {
      setLoading(false);
    }
  };

  const togglePresence = async (rosterId: string, currentPresence: boolean) => {
    try {
      await gameRosterService.updatePlayerPresence(rosterId, !currentPresence);
      setRoster(prev => prev.map(p => 
        p.id === rosterId ? { ...p, is_present: !currentPresence } : p
      ));
      toast.success(`Player marked as ${!currentPresence ? 'present' : 'absent'}`);
      onRosterUpdate?.();
    } catch (error) {
      console.error('Error updating presence:', error);
      toast.error('Failed to update player presence');
    }
  };

  const removePlayer = async (rosterId: string) => {
    try {
      await gameRosterService.removePlayerFromGameRoster(rosterId);
      setRoster(prev => prev.filter(p => p.id !== rosterId));
      toast.success('Player removed from game roster');
      onRosterUpdate?.();
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error('Failed to remove player');
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadRoster();
    }
  }, [isOpen, gameId]);

  const homeTeamPlayers = roster.filter(p => p.team_side === 'home');
  const awayTeamPlayers = roster.filter(p => p.team_side === 'away');
  const presentPlayers = roster.filter(p => p.is_present);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Users className="h-5 w-5" />
            Game Roster Management
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="text-blue-400 border-blue-400">
                {presentPlayers.length} Present
              </Badge>
              <Badge variant="outline" className="text-gray-400 border-gray-400">
                {roster.length} Total
              </Badge>
            </div>
            <Button
              onClick={populateFromTeams}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Populate from Teams
            </Button>
          </div>

          {/* Home Team */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-blue-400">Home Team</h3>
            {homeTeamPlayers.length > 0 ? (
              <div className="grid gap-2">
                {homeTeamPlayers.map((player) => (
                  <div
                    key={player.id}
                    className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {player.is_present ? (
                        <UserCheck className="h-4 w-4 text-green-400" />
                      ) : (
                        <UserX className="h-4 w-4 text-red-400" />
                      )}
                      <div>
                        <p className="font-medium text-white">
                          {player.player_name}
                        </p>
                        {player.position && (
                          <Badge variant="outline" className="text-xs">
                            {player.position}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={player.is_present}
                        onCheckedChange={() => togglePresence(player.id, player.is_present)}
                        className="data-[state=checked]:bg-green-600"
                      />
                      <Button
                        onClick={() => removePlayer(player.id)}
                        size="sm"
                        variant="destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No home team players added</p>
            )}
          </div>

          {/* Away Team */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-red-400">Away Team</h3>
            {awayTeamPlayers.length > 0 ? (
              <div className="grid gap-2">
                {awayTeamPlayers.map((player) => (
                  <div
                    key={player.id}
                    className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {player.is_present ? (
                        <UserCheck className="h-4 w-4 text-green-400" />
                      ) : (
                        <UserX className="h-4 w-4 text-red-400" />
                      )}
                      <div>
                        <p className="font-medium text-white">
                          {player.player_name}
                        </p>
                        {player.position && (
                          <Badge variant="outline" className="text-xs">
                            {player.position}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={player.is_present}
                        onCheckedChange={() => togglePresence(player.id, player.is_present)}
                        className="data-[state=checked]:bg-green-600"
                      />
                      <Button
                        onClick={() => removePlayer(player.id)}
                        size="sm"
                        variant="destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No away team players added</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};